import isNil from 'lodash/fp/isNil';

export const parseFloatingNumber = (value: string | number): number =>
	typeof value === 'number' ? value : Number(value.replace(/[^\d.,-]/gi, '').replace(',', '.'));

export const trimNonNumeric = (value: string) => value.replace(/\D/gi, '');

export const formatNumber = (value?: string | number, fractionDigits = 2) =>
	isNil(value)
		? ''
		: Number(value).toLocaleString('ru-RU', {
				maximumFractionDigits: fractionDigits,
		  });

class NumbersRange {
	public numbers: number[] = [];

	constructor(private from: number, private to: number) {
		for (let i = this.from; i <= this.to; i++) {
			this.numbers.push(i);
		}
	}

	contains(value: number) {
		return value >= this.from && value <= this.to;
	}

	length() {
		return this.to - this.from + 1;
	}
}

export class NumbersSequence {
	private numbers = new Set<number>();
	private ranges = new Set<NumbersRange>();

	contains(value: number) {
		if (this.numbers.has(value)) {
			return true;
		}

		let found = false;

		this.ranges.forEach((range) => {
			if (!found && range.contains(value)) {
				found = true;
			}
		});

		return found;
	}

	add(value: number | string | NumbersSequence) {
		if (typeof value === 'number') return this.numbers.add(value);

		if (typeof value === 'string') {
			if (value.includes('..')) {
				const [from, to] = value.split('..').map(Number);

				return this.ranges.add(new NumbersRange(from, to));
			} else {
				return this.numbers.add(Number(value));
			}
		}

		value.numbers.forEach((number) => this.numbers.add(number));
		value.ranges.forEach((range) => this.ranges.add(range));
	}

	intersection(other: NumbersSequence): NumbersSequence {
		const result = new NumbersSequence();

		this.numbers.forEach((num) => {
			if (other.contains(num)) result.add(num);
		});

		this.ranges.forEach((range) => {
			range.numbers.forEach((num) => {
				if (other.contains(num)) result.add(num);
			});
		});

		return result;
	}

	subtract(b: NumbersSequence): NumbersSequence {
		const result = new NumbersSequence();

		this.numbers.forEach((num) => {
			if (!b.contains(num)) result.add(num);
		});

		this.ranges.forEach((range) => {
			range.numbers.forEach((num) => {
				if (!b.contains(num)) result.add(num);
			});
		});

		return result;
	}

	length() {
		let rangesLength = 0;

		this.ranges.forEach((range) => {
			rangesLength += range.length();
		});

		return this.numbers.size + rangesLength;
	}

	static fromString(value: string): NumbersSequence {
		const sequence = new NumbersSequence();

		value
			.trim()
			.replaceAll(/[\\[\]]/gi, '')
			.split(',')
			.forEach((item) => sequence.add(item));

		return sequence;
	}
}

export const numberSpaceFormatter = <T extends number | string>(value: T) => {
	const format = (pattern: string) => {
		let i = 0;
		let stringValue = value.toString();

		return pattern.replace(/\S/g, () => stringValue[i++] || '');
	};

	const toBik = () => format('### ### ###');

	const toAccountNumber = () => format('##### ### # #### #######');

	return {
		toBik,
		toAccountNumber,
	};
};
